import {
  Box,
  Button,
  FilledInput,
  FormControl,
  InputLabel,
  Typography,
} from "@mui/material";
import React from "react";
import { useCookies } from "react-cookie";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import notify from "devextreme/ui/notify";
import { Navigate } from "react-router-dom";
import { LoadingButton } from "@mui/lab";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { login } from "../../BusinessObjects/Redux/features/user/userSlice";
import UserProfile from "../../Models/UserProfile";
import uuid from "react-uuid";
import { RootState } from "../../BusinessObjects/Redux/app/store";
import cubeApiClient from "../../BusinessObjects/cubeApiService";
import { useMutation } from "react-query";
import LoggedContext from "../../BusinessObjects/LoggedContext";
import { UserCompany } from "../../Models/SysConf/UserCompany";

const Impersonate = () => {
  const loggedContext = React.useContext(LoggedContext);

  const isLoggedIn = useSelector((state: RootState) => state.user.isLoggedIn);

  const refDefaultPage = React.useRef<string>("");

  const dispatch = useDispatch();

  const [cookies, setCookie, removeCookie] = useCookies(["authToken"]);

  const [authToken] = React.useState<string | null>(cookies.authToken);
  const [externalId, setExternalId] = React.useState<string>("");

  const impersonateMutation = useMutation({
    mutationFn: (data: { authToken: string; externalId: string }) => {
      let c = new cubeApiClient();
      return c.impersonate(data.authToken, data.externalId);
    },
    onError: (error: any) => {
      let errorMessage = "An error occurred.";

      if (axios.isAxiosError(error)) {
        let data = error.response?.data;

        if (data) {
          if (data.ErrorMessage) {
            errorMessage = data.ErrorMessage;
          } else if (data.Message) {
            errorMessage = data.Message;
          }

          // Se nella data è presente un nuovo RememberToken, lo salvo nei cookies
          if (data.RememberToken) {
            setCookie("authToken", data.RememberToken, {
              path: "/",
            });

            // Forzo il refresh della pagina
            window.location.reload();
          } else {
            // Se non è presente significa che il token è scaduto/invalido, quindi lo rimuovo
            removeCookie("authToken", { path: "/" });
          }
        }
      }

      notify(errorMessage, "error", 3000);
    },
  });

  React.useEffect(() => {
    if (impersonateMutation.data) {
      if (impersonateMutation.data.RememberToken) {
        // Mi salvo nei cookie il token
        setCookie("authToken", impersonateMutation.data.RememberToken, {
          path: "/",
          maxAge: 60 * 60 * 24 * 365,
        });
      }

      let userCompanies: UserCompany[] =
        impersonateMutation.data && impersonateMutation.data.UserCompanies
          ? JSON.parse(impersonateMutation.data.UserCompanies)
          : [];

      let userProfile: UserProfile | undefined =
        impersonateMutation.data && impersonateMutation.data.UserProfile
          ? JSON.parse(impersonateMutation.data.UserProfile)
          : undefined;

      let myUserCompany: UserCompany | undefined = undefined;
      if (
        userCompanies &&
        userCompanies.length > 0 &&
        impersonateMutation.data.userName
      ) {
        myUserCompany = userCompanies.find(
          (uc: UserCompany) => uc.UserName == impersonateMutation.data.userName
        );
      }

      let sessionId = uuid().replaceAll("-", "");
      let protectionLevel = 0;
      let defOpenPageQuery: string = "";

      // ProtectionLevel:
      // 1 - prendere quello della UserCompanies(myUserCompany)
      // 2 - Se non c'è prendere quello dalla UserProfile
      // 3 - 0
      if (myUserCompany) protectionLevel = myUserCompany.ProtectionLevel;
      else if (userProfile) protectionLevel = userProfile.ProtectionLevel;
      else protectionLevel = 0;

      if (myUserCompany) {
        if (myUserCompany.LandingPageMenuItem_ID)
          defOpenPageQuery = "?menu_id=" + myUserCompany.LandingPageMenuItem_ID;
        else if (myUserCompany.LandingPageResource_ID)
          defOpenPageQuery =
            "?resource_id=" + myUserCompany.LandingPageResource_ID;
        else defOpenPageQuery = "";
      }

      refDefaultPage.current = defOpenPageQuery;

      //Controllo se c'è il locate
      if (userProfile && userProfile.LocateString)
        sessionStorage.setItem("locale", userProfile.LocateString);
      else {
        let language = navigator.language.substring(0, 2).toLocaleLowerCase();
        sessionStorage.setItem("locale", language);
      }

      if (userProfile) {
        userProfile.ProtectionLevel = protectionLevel;
      }

      let tokenApi = impersonateMutation.data.access_token;

      loggedContext.setUserProfile(userProfile);
      loggedContext.setUserToken(impersonateMutation.data.userName, tokenApi);

      dispatch(
        login({
          tokenAPI: tokenApi,
          userProfile: userProfile,
          sessionId,
        })
      );
    }
  }, [impersonateMutation.data, dispatch, setCookie]);

  const _onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!externalId || !authToken) return;
    impersonateMutation.mutate({ authToken, externalId });
  };

  if (!authToken) return <div>Auth Token non trovato</div>;
  if (isLoggedIn) return <Navigate to={`/home${refDefaultPage.current}`} />;

  return (
    <div className="container-fluid pb-120" id="login-page">
      <div className="w-100 d-flex justify-content-center align-items-center mt-5">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{
            width: "50%",
            maxWidth: "335px",
          }}
        >
          <LazyLoadImage
            src={`images/e-cube/logo-color.png`}
            alt={"Logo"}
            effect="blur"
            className="img-fluid"
          />
        </div>
      </div>
      <div className="w-100 d-flex justify-content-center align-items-center mt-5">
        <form className="login-form w-50" onSubmit={_onSubmit}>
          <div className="form-group">
            <FormControl
              variant="filled"
              fullWidth
              margin="normal"
              disabled={impersonateMutation.isLoading}
            >
              <InputLabel htmlFor="externalId">
                Scansiona il tuo codice personale
              </InputLabel>
              <FilledInput
                id="externalId"
                autoFocus
                name="externalId"
                type={"text"}
                disableUnderline
                value={externalId}
                onChange={(e) => setExternalId(e.target.value)}
              />
            </FormControl>
          </div>
          <div className="form-group">
            <LoadingButton
              variant="contained"
              color="primary"
              size="large"
              disableElevation
              fullWidth
              style={{
                marginTop: 32,
                padding: 16,
                fontWeight: "bold",
                borderRadius: 20,
              }}
              type="submit"
              loading={impersonateMutation.isLoading}
            >
              <span>Accedi</span>
            </LoadingButton>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Impersonate;
